import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Info,
  Loader2,
  MapPin,
  ShieldCheck,
  Star,
  Wrench,
  ExternalLink,
  Handshake,
  ClipboardCheck,
  Heart,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { api } from "../lib/api";
import { formatEUR, formatKm, countryFlag, statusStyle } from "../lib/format";
import OpportunityBadge from "../components/OpportunityBadge";
import AIScoreDial from "../components/AIScoreDial";
import PhotoGallery from "../components/PhotoGallery";
import PriceHistoryChart from "../components/PriceHistoryChart";
import TrattativaModal from "../components/TrattativaModal";
import { useFavorites } from "../lib/favorites";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Sparkles as SparklesIcon } from "lucide-react";
import { useTranslation as _useI18n } from "react-i18next";

function AIExplanation({ vehicleId, language, fallback }) {
  const { t } = _useI18n();
  const [text, setText] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [isFallback, setIsFallback] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setIsFallback(false);
    api
      .get(`/vehicles/${vehicleId}/explanation`, { params: { language } })
      .then((r) => {
        if (cancelled) return;
        setText(r.data?.text || fallback);
        setIsFallback(!!r.data?.fallback);
      })
      .catch(() => {
        if (cancelled) return;
        setText(fallback);
        setIsFallback(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [vehicleId, language, fallback]);

  if (loading) {
    return (
      <div data-testid="ai-explanation-loading" className="mt-1.5 space-y-2">
        <div className="h-3 bg-slate-100 rounded animate-pulse w-11/12" />
        <div className="h-3 bg-slate-100 rounded animate-pulse w-10/12" />
        <div className="h-3 bg-slate-100 rounded animate-pulse w-8/12" />
      </div>
    );
  }
  return (
    <div data-testid="ai-explanation" className="mt-1.5">
      <p className="text-slate-800 leading-relaxed">{text}</p>
      {isFallback && (
        <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          {t("ai.fallback_notice")}
        </p>
      )}
      <p
        data-testid="ai-explanation-disclaimer"
        className="mt-2 text-[10px] italic text-slate-400 leading-snug"
      >
        {t("ai.disclaimer")}
      </p>
    </div>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trattativaOpen, setTrattativaOpen] = useState(false);
  const [trattativaDefaultType, setTrattativaDefaultType] = useState("acquisto");
  const { favorites, toggle } = useFavorites();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/vehicles/${id}`)
      .then((r) => setV(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!v) return <div className="py-16 text-center text-sm text-slate-500">404</div>;

  const a = v.analysis || {};
  const destCountry = a.destination_country || (v.country === "DE" ? "IT" : "DE");
  const destMarketValue =
    destCountry === "IT" ? v.market_value_it : v.market_value_de;

  const marginPositive = (a.estimated_margin_eur || 0) >= 0;
  const isFav = favorites.has(v.id);

  const copyVin = async () => {
    try {
      await navigator.clipboard.writeText(v.vin);
      toast.success(t("vehicle.vin_copied"));
    } catch {
      toast.error(t("common.error_generic"));
    }
  };

  const openStartNegotiation = () => {
    setTrattativaDefaultType("acquisto");
    setTrattativaOpen(true);
  };
  const openCheckAvailability = () => {
    setTrattativaDefaultType("solo_verifica");
    setTrattativaOpen(true);
  };
  const openAskAI = () => {
    navigate(
      `/ai?vehicle=${v.id}&prompt=${encodeURIComponent(
        t("ai.ask_about_this", { brand: v.brand, model: v.model })
      )}`
    );
  };

  return (
    <div className="py-6 md:py-10 pb-32 md:pb-10" data-testid={`vehicle-detail-${v.id}`}>
      <Link
        to="/marketplace"
        data-testid="back-to-marketplace"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t("vehicle.back")}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <PhotoGallery
            images={v.images || []}
            videoUrl={v.video_url}
            alt={`${v.brand} ${v.model}`}
          />

          {/* Header + hero AI panel */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm"
            data-testid="vehicle-hero"
          >
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span
                className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md ${statusStyle(
                  v.status
                )}`}
                data-testid="vehicle-status"
              >
                {t(`status.${v.status}`)}
              </span>
              <span className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-md px-2 py-1">
                {countryFlag(v.country)} {v.country}
              </span>
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-slate-950">
              {v.brand} {v.model}
            </h1>
            <p className="text-slate-500 mt-1">{v.trim}</p>

            {/* Human one-liner pricing — the star of the primary view */}
            <div
              data-testid="vehicle-hero-pricing"
              className="mt-5 rounded-xl bg-slate-50 border border-slate-100 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
            >
              <PricingBlock
                label={t("card.buy_at")}
                value={formatEUR(v.price_eur)}
                testid="vehicle-hero-buy"
              />
              <PricingBlock
                label={t("card.resell_at")}
                value={`~${formatEUR(destMarketValue || 0)}`}
                testid="vehicle-hero-resell"
              />
              <PricingBlock
                label={
                  <span className="inline-flex items-center gap-1">
                    {t("card.gain_estimated")}
                    <span
                      data-testid="vehicle-hero-gain-demo-tag"
                      className={`normal-case tracking-normal text-[9px] font-medium italic rounded-full px-1.5 py-0.5 border ${
                        v.is_demo
                          ? "text-amber-700 bg-amber-50 border-amber-200"
                          : "text-slate-500 bg-slate-50 border-slate-200"
                      }`}
                    >
                      {t(v.is_demo ? "dataTag.demo_short" : "dataTag.estimate_short")}
                    </span>
                  </span>
                }
                value={
                  <span
                    className={`font-heading font-bold text-2xl tabular-nums ${
                      marginPositive ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    {marginPositive ? "~+" : "~"}
                    {formatEUR(a.estimated_margin_eur || 0)}
                  </span>
                }
                testid="vehicle-hero-gain"
                large
              />
            </div>

            {/* Primary CTA + secondary actions */}
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Button
                data-testid="vehicle-hero-la-voglio"
                onClick={openStartNegotiation}
                className="rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-6 text-sm font-semibold flex-1 min-w-[200px] sm:flex-initial"
              >
                {t("card.la_voglio")}
              </Button>
              <Button
                data-testid="vehicle-hero-check-availability"
                variant="outline"
                onClick={openCheckAvailability}
                className="rounded-full h-12 px-5 text-sm font-semibold"
              >
                <ClipboardCheck className="w-4 h-4 mr-1.5" />
                {t("actions.check_availability")}
              </Button>
              <Button
                data-testid="vehicle-hero-ask-ai"
                variant="ghost"
                onClick={openAskAI}
                className="rounded-full h-12 px-4 text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                <SparklesIcon className="w-4 h-4 mr-1.5" />
                {t("actions.ask_ai")}
              </Button>
            </div>

            {/* Advanced AI analysis (collapsed by default) */}
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              data-testid="vehicle-detail-advanced-toggle"
              aria-expanded={showAdvanced}
              className="mt-6 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 border-t border-slate-100 pt-4"
            >
              {showAdvanced ? t("vehicle.advanced_hide") : t("vehicle.advanced_show")}
              {showAdvanced ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </button>

            <AnimatePresence initial={false}>
              {showAdvanced && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  data-testid="vehicle-detail-advanced-panel"
                  className="overflow-hidden"
                >
                  <div className="mt-4 flex flex-wrap items-center gap-2 mb-3">
                    <OpportunityBadge index={a.opportunity_index} score={a.ai_score} />
                    <InfoTooltip
                      testid="vehicle-ai-score-tooltip"
                      text={t("vehicle.tooltip_ai_score")}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
                    <div className="flex md:block items-center gap-5">
                      <AIScoreDial score={a.ai_score} size={128} />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                        {t("vehicle.ai_verdict")}
                        <span
                          data-testid="vehicle-detail-ai-demo-tag"
                          className={`ml-2 normal-case tracking-normal text-[10px] font-medium italic rounded-full px-1.5 py-0.5 border ${
                            v.is_demo
                              ? "text-amber-700 bg-amber-50 border-amber-200"
                              : "text-slate-500 bg-slate-50 border-slate-200"
                          }`}
                        >
                          {t(v.is_demo ? "dataTag.demo_full" : "dataTag.estimate_full")}
                        </span>
                      </div>
                      <AIExplanation
                        vehicleId={v.id}
                        language={i18n.language}
                        fallback={a.explanation_text}
                      />
                      <button
                        data-testid="ask-ai-about-this-car"
                        onClick={() =>
                          navigate(
                            `/ai?vehicle=${v.id}&prompt=${encodeURIComponent(
                              t("ai.ask_about_this", { brand: v.brand, model: v.model })
                            )}`
                          )
                        }
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 border border-blue-100 bg-blue-50 hover:bg-blue-100 rounded-full px-3 py-1.5 transition-colors"
                      >
                        <SparklesIcon className="w-3 h-3" />
                        {t("ai.ask_about_button")}
                      </button>
                      <div className="mt-4 grid grid-cols-3 gap-3">
                        <MiniKPI
                          label={t("vehicle.roi")}
                          value={`${a.roi_percent?.toFixed(1)}%`}
                          accent="text-blue-600"
                        />
                        <MiniKPI
                          label={t("vehicle.avg_resale_days")}
                          value={`${a.avg_resale_days}g`}
                        />
                        <MiniKPI
                          label={
                            <span className="inline-flex items-center gap-1">
                              {t("vehicle.risk")}
                              <InfoTooltip
                                testid="vehicle-risk-tooltip"
                                text={t("vehicle.tooltip_risk")}
                                size="xs"
                              />
                            </span>
                          }
                          value={t(`vehicle.risk_${a.risk_level}`, a.risk_level)}
                          accent={
                            a.risk_level === "low"
                              ? "text-emerald-600"
                              : a.risk_level === "high"
                              ? "text-red-600"
                              : "text-orange-600"
                          }
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Spec grid */}
          <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
            <h3 className="font-heading font-semibold text-lg tracking-tight text-slate-950 mb-4">
              {t("vehicle.specs_title")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Stat label={t("vehicle.year")} value={v.year} />
              <Stat label={t("vehicle.km")} value={formatKm(v.km)} />
              <Stat label={t("vehicle.fuel")} value={t(`fuel.${v.fuel}`, v.fuel)} />
              <Stat label={t("vehicle.power")} value={`${v.power_cv} CV`} />
              <Stat label={t("vehicle.transmission")} value={v.transmission} />
              <Stat label={t("vehicle.color")} value={v.color} />
              <Stat label={t("vehicle.market_value_it")} value={formatEUR(v.market_value_it)} />
              <Stat label={t("vehicle.market_value_de")} value={formatEUR(v.market_value_de)} />
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                    VIN
                  </div>
                  <div
                    className="mt-0.5 font-mono text-xs text-slate-900 break-all"
                    data-testid="vehicle-vin"
                  >
                    {v.vin}
                  </div>
                </div>
                <button
                  data-testid="vehicle-vin-copy"
                  onClick={copyVin}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 border border-slate-200 rounded-full px-3 py-1.5 bg-white hover:bg-slate-50 transition-colors"
                >
                  <Copy className="w-3 h-3" /> {t("common.copy")}
                </button>
              </div>
            </div>
            <p className="mt-6 text-sm text-slate-600 leading-relaxed">{v.description}</p>
          </div>

          {/* Options */}
          {v.options?.length > 0 && (
            <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
              <h3 className="font-heading font-semibold text-lg tracking-tight text-slate-950 mb-3">
                {t("vehicle.options")}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {v.options.map((o) => (
                  <span
                    key={o}
                    className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 bg-slate-50 border border-slate-100 rounded-full px-3 py-1.5"
                  >
                    <Check className="w-3 h-3 text-emerald-600" />
                    {o}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Maintenance timeline */}
          {v.maintenance_history?.length > 0 && (
            <div
              data-testid="maintenance-timeline"
              className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm"
            >
              <h3 className="font-heading font-semibold text-lg tracking-tight text-slate-950 mb-4 inline-flex items-center gap-2">
                <Wrench className="w-4 h-4 text-blue-600" />
                {t("vehicle.maintenance")}
              </h3>
              <ol className="relative border-l border-slate-200 ml-2 space-y-5">
                {v.maintenance_history.map((m, i) => (
                  <li key={i} className="pl-5">
                    <div className="absolute -left-[7px] w-3.5 h-3.5 rounded-full bg-white border-2 border-blue-500" />
                    <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                      <CalendarDays className="w-3 h-3" />
                      {new Date(m.date).toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                      <span className="text-slate-300">·</span>
                      <span className="tabular-nums">{formatKm(m.km)}</span>
                    </div>
                    <div className="mt-1 text-sm text-slate-800">{m.description}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Price history */}
          {v.price_history?.length > 1 && (
            <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
              <PriceHistoryChart history={v.price_history} />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4 lg:sticky lg:top-20 h-fit">
          {/* Financial breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            data-testid="financial-breakdown"
            className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm"
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
              {t("vehicle.price")}
            </div>
            <div className="mt-1 font-heading font-bold text-3xl tracking-tight tabular-nums text-slate-950">
              {formatEUR(v.price_eur)}
            </div>

            <div className="mt-5 space-y-2 text-sm">
              <Row label={t("vehicle.purchase_price")} value={formatEUR(v.price_eur)} />
              <Row label={t("vehicle.transport")} value={formatEUR(v.transport_cost)} />
              <Row label={t("vehicle.registration")} value={formatEUR(v.registration_cost)} />
              {v.vat_applicable ? (
                <Row label={t("vehicle.vat")} value={formatEUR(v.vat_amount)} />
              ) : (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{t("vehicle.vat")}</span>
                  <span className="italic text-slate-400 text-right">
                    {t("vehicle.vat_margin_regime")}
                  </span>
                </div>
              )}
              <div className="pt-2 mt-2 border-t border-slate-100" />
              <Row
                label={t("vehicle.total_cost")}
                value={formatEUR(a.landed_cost || v.price_eur)}
                strong
                testid="fin-total-cost"
              />
              <div className="pt-2 mt-2 border-t border-slate-100" />
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold pt-1">
                {t("vehicle.destination_market_value")} · {destCountry}
              </div>
              <Row label={t("vehicle.market_value_it")} value={formatEUR(v.market_value_it)} muted={destCountry !== "IT"} />
              <Row label={t("vehicle.market_value_de")} value={formatEUR(v.market_value_de)} muted={destCountry !== "DE"} />
              <Row
                label={t("vehicle.destination_value_short")}
                value={formatEUR(destMarketValue || 0)}
                strong
              />
              <div className="pt-2 mt-2 border-t border-slate-100" />
              <Row
                label={
                  <span className="inline-flex items-center gap-2">
                    <span>{t("vehicle.margin")}</span>
                    <span
                      data-testid="vehicle-detail-margin-demo-tag"
                      className={`normal-case tracking-normal text-[10px] font-medium italic rounded-full px-1.5 py-0.5 border ${
                        v.is_demo
                          ? "text-amber-700 bg-amber-50 border-amber-200"
                          : "text-slate-500 bg-slate-50 border-slate-200"
                      }`}
                    >
                      {t(v.is_demo ? "dataTag.demo_full" : "dataTag.estimate_full")}
                    </span>
                  </span>
                }
                testid="fin-margin"
                value={
                  <span className={`font-heading font-bold text-xl tabular-nums ${marginPositive ? "text-emerald-600" : "text-red-600"}`}>
                    {marginPositive ? "+" : ""}
                    {formatEUR(a.estimated_margin_eur || 0)}
                  </span>
                }
              />
              <Row label={t("vehicle.roi")} value={`${a.roi_percent?.toFixed(1)}%`} />
              <Row label={t("vehicle.avg_resale_days")} value={`${a.avg_resale_days} g`} />
              <Row
                label={t("vehicle.risk")}
                value={
                  <span
                    className={
                      a.risk_level === "low"
                        ? "text-emerald-600"
                        : a.risk_level === "high"
                        ? "text-red-600"
                        : "text-orange-600"
                    }
                  >
                    {t(`vehicle.risk_${a.risk_level}`, a.risk_level)}
                  </span>
                }
              />
            </div>

            <div className="hidden md:flex flex-col gap-2 mt-6">
              <Button
                data-testid="action-start-negotiation-desktop"
                onClick={openStartNegotiation}
                className="rounded-full bg-blue-600 hover:bg-blue-700 h-11 font-semibold"
              >
                <Handshake className="w-4 h-4 mr-1.5" />
                {t("card.la_voglio")}
              </Button>
              <Button
                data-testid="action-check-availability-desktop"
                variant="outline"
                onClick={openCheckAvailability}
                className="rounded-full h-11 font-semibold"
              >
                <ClipboardCheck className="w-4 h-4 mr-1.5" />
                {t("actions.check_availability")}
              </Button>
              <Button
                data-testid="action-ask-ai-desktop"
                variant="ghost"
                onClick={openAskAI}
                className="rounded-full h-11 font-semibold text-blue-600 hover:text-blue-700"
              >
                <SparklesIcon className="w-4 h-4 mr-1.5" />
                {t("actions.ask_ai")}
              </Button>
              <button
                type="button"
                onClick={() => toggle(v.id)}
                data-testid="vehicle-detail-fav"
                aria-pressed={isFav}
                className={`inline-flex items-center justify-center gap-1.5 rounded-full h-10 mt-1 text-sm font-semibold transition-colors border ${
                  isFav
                    ? "bg-rose-50 text-rose-600 border-rose-200"
                    : "text-slate-500 border-slate-200 hover:text-rose-600 hover:border-rose-200"
                }`}
              >
                <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
                {isFav ? t("vehicle.saved") : t("vehicle.save")}
              </button>
            </div>
          </motion.div>

          {/* Dealer trust card */}
          {v.dealer && (
            <Link
              to={`/dealers/${v.dealer.id}`}
              data-testid="dealer-trust-card"
              className="block rounded-2xl bg-white border border-slate-100 p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                    {t("vehicle.dealer")}
                  </div>
                  <div className="mt-1 font-heading font-semibold text-lg tracking-tight text-slate-950 inline-flex items-center gap-1.5">
                    {v.dealer.name}
                    {v.dealer.verified && <BadgeCheck className="w-4 h-4 text-blue-600" />}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />
                    {countryFlag(v.dealer.country)} {v.dealer.city}
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </div>
              <div className="mt-4 flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
                  <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  {v.dealer.rating?.toFixed(1)}
                  <span className="text-slate-400 font-normal">
                    ({v.dealer.reviews_count})
                  </span>
                </span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">
                  {v.dealer.years_active} {t("dealer.years_short")}
                </span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500 inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-emerald-500" />
                  {v.dealer.vehicles_count} {t("dealer.vehicles_short")}
                </span>
              </div>
            </Link>
          )}
        </aside>
      </div>

      {/* Sticky mobile action bar */}
      <div
        data-testid="mobile-action-bar"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-xl px-3 py-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 60px)" }}
      >
        <div className="flex gap-2">
          <Button
            data-testid="action-start-negotiation-mobile"
            onClick={openStartNegotiation}
            className="flex-1 rounded-full h-11 bg-blue-600 hover:bg-blue-700 text-sm font-semibold"
          >
            <Handshake className="w-4 h-4 mr-1.5" />
            {t("card.la_voglio")}
          </Button>
          <Button
            data-testid="action-check-availability-mobile"
            variant="outline"
            onClick={openCheckAvailability}
            className="rounded-full h-11 w-11 p-0 shrink-0"
            aria-label={t("actions.check_availability")}
          >
            <ClipboardCheck className="w-4 h-4" />
          </Button>
          <Button
            data-testid="action-ask-ai-mobile"
            variant="outline"
            onClick={openAskAI}
            className="rounded-full h-11 w-11 p-0 shrink-0"
            aria-label={t("actions.ask_ai")}
          >
            <SparklesIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <TrattativaModal
        open={trattativaOpen}
        onOpenChange={setTrattativaOpen}
        vehicle={v}
        defaultType={trattativaDefaultType}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}

function PricingBlock({ label, value, testid, large }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div
        className={`mt-0.5 tabular-nums text-slate-950 ${
          large ? "" : "font-heading font-bold text-xl"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function MiniKPI({ label, value, accent }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
        {label}
      </div>
      <div className={`mt-0.5 font-heading font-bold text-sm tabular-nums ${accent || "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function Row({ label, value, strong, muted, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={muted ? "text-slate-400" : "text-slate-500"}>{label}</span>
      <span
        className={`tabular-nums ${
          muted
            ? "text-slate-400"
            : strong
            ? "font-semibold text-slate-950"
            : "text-slate-700"
        }`}
      >
        {value}
      </span>
    </div>
  );
}


function InfoTooltip({ text, testid, size = "sm" }) {
  const dim = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="info"
            data-testid={testid}
            className="inline-flex items-center justify-center text-slate-400 hover:text-slate-600 focus:outline-none focus:text-slate-600"
          >
            <Info className={dim} />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
